import React, { useState } from 'react';
import { ArrowLeft, Terminal, LoaderCircle, Check, Archive, Cloud, Rocket, Server, X, Plus } from 'lucide-react';
import { generateRandomSuffix, validateSubdomainPrefix } from '../../utils/subdomainHelper';
import '../../styles/premium-deploy.css';

export default function ComposeDeploy({
  user, onStatusMessage, onBack, api,
  beginDeploymentTracking, openComposeWorkspace,
  deploymentSessionId, deploymentSession
}) {
  const [appName, setAppName] = useState('');
  const [appNameError, setAppNameError] = useState('');
  const [platformSuffix] = useState(() => generateRandomSuffix());
  const [subdomainPrefix, setSubdomainPrefix] = useState('');
  const [subdomainError, setSubdomainError] = useState('');
  const [domain, setDomain] = useState('');
  const [domainError, setDomainError] = useState('');
  const [composeCpu, setComposeCpu] = useState('1.0');

  const handleSubdomainChange = (e) => {
    const val = e.target.value;
    setSubdomainPrefix(val);
    const res = validateSubdomainPrefix(val);
    setSubdomainError(res.valid ? '' : res.error);
  };

  const validateDomain = (val) => {
    if (!val || !val.trim()) {
      setDomainError('');
      return true;
    }
    if (/devorants\.com$/i.test(val.trim())) {
      setDomainError('devorants.com subdomains cannot be used as custom domains.');
      return false;
    }
    setDomainError('');
    return true;
  };

  const handleDomainChange = (e) => {
    const val = e.target.value;
    setDomain(val);
    validateDomain(val);
  };
  const [composeMemory, setComposeMemory] = useState('512m');
  const [composeInternalPort, setComposeInternalPort] = useState('80');
  const [composeZipFile, setComposeZipFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const validateAppName = (val) => {
    if (!val) {
      setAppNameError('');
      return false;
    }
    if (/\s/.test(val)) {
      setAppNameError('Spaces are not allowed. Use hyphens (-) instead.');
      return false;
    }
    if (/[A-Z]/.test(val)) {
      setAppNameError('Uppercase letters are not allowed. Use lowercase only.');
      return false;
    }
    if (!/^[a-z0-9-]+$/.test(val)) {
      setAppNameError('Only lowercase letters, numbers, and hyphens (-) are allowed.');
      return false;
    }
    setAppNameError('');
    return true;
  };

  const handleAppNameChange = (e) => {
    const val = e.target.value;
    setAppName(val);
    validateAppName(val);
  };

  // Multi-entry point management
  const [entryPoints, setEntryPoints] = useState([]);

  const handleAddEntryPoint = () => {
    setEntryPoints([...entryPoints, { port: '', role: 'service', domain: '' }]);
  };

  const handleUpdateEntryPoint = (idx, field, value) => {
    const updated = [...entryPoints];
    updated[idx] = { ...updated[idx], [field]: field === 'port' ? (value === '' ? '' : Number(value)) : value };
    setEntryPoints(updated);
  };

  const handleRemoveEntryPoint = (idx) => {
    setEntryPoints(entryPoints.filter((_, i) => i !== idx));
  };

  const handleDeployCompose = async (e) => {
    e.preventDefault();
    if (!appName || !validateAppName(appName)) {
      onStatusMessage(appNameError || 'Please enter a valid Workspace name', 'error');
      return;
    }

    if (subdomainPrefix && !validateSubdomainPrefix(subdomainPrefix).valid) {
      onStatusMessage(subdomainError || 'Invalid subdomain format.', 'error');
      return;
    }

    if (domain && !validateDomain(domain)) {
      onStatusMessage(domainError || 'devorants.com subdomains cannot be used as custom domains.', 'error');
      return;
    }

    const resolvedPrefix = (subdomainPrefix && subdomainPrefix.trim())
      ? subdomainPrefix.trim().toLowerCase()
      : (appName || 'app').trim().toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');
    const autoPlatformDomain = `${resolvedPrefix}-${platformSuffix}.devorants.com`;
    const finalDomain = (domain && domain.trim()) ? domain.trim() : autoPlatformDomain;

    setLoading(true);

    try {
      await api.checkDeploymentQuota({
        cpu: composeCpu,
        memory: composeMemory
      });
    } catch (err) {
      onStatusMessage(err.message, 'error');
      setLoading(false);
      return;
    }

    const deploymentId = beginDeploymentTracking('compose', appName);

    // Filter valid entry points (must have a port)
    const validEntryPoints = entryPoints.filter(ep => ep.port && Number(ep.port) > 0);

    // Immediately open the workspace in deploying mode
    openComposeWorkspace(appName, { isDeploying: true, domain: finalDomain, autoDeploy: true });

    try {
      api.deployComposeEnv(appName, finalDomain, {
        deploymentId,
        file: composeZipFile,
        cpu: composeCpu,
        memory: composeMemory,
        internalPort: composeInternalPort,
        entryPoints: validEntryPoints.length > 0 ? validEntryPoints : undefined
      }).then(
        (data) => {
          if (data && (data.success || data.message || data.port)) {
            // Update the workspace info with the actual connection details
            openComposeWorkspace(appName, { ...data, autoDeploy: true });
          }
        },
        (err) => {
          onStatusMessage(`Error: ${err.message}`, 'error');
        }
      ).finally(() => {
        setLoading(false);
      });
    } catch (err) {
      onStatusMessage(`Error: ${err.message}`, 'error');
      setLoading(false);
    }
  };

  return (
    <div className="vercel-form-body">
      <button type="button" className="vercel-back-btn-new" onClick={onBack}>
        <ArrowLeft size={16} />
        <span>Back to selection</span>
      </button>


        <form onSubmit={handleDeployCompose} className="vercel-premium-form">
          <div className="form-section-group">
            <div className="form-section-header">
              <Archive size={18} />
              <h3>General Configuration</h3>
            </div>
            <div className="form-row-grid">
              <div className="form-group">
                <label>Workspace Name <span className="required-star">*</span></label>
                <input 
                  type="text" 
                  placeholder="e.g. my-compose-stack" 
                  required 
                  value={appName} 
                  onChange={handleAppNameChange} 
                  style={appNameError ? { borderColor: 'var(--danger, #ef4444)' } : {}}
                />
                {appNameError ? (
                  <p className="form-feedback" style={{ color: 'var(--danger, #ef4444)', marginTop: '0.25rem', fontWeight: 500 }}>
                    {appNameError}
                  </p>
                ) : (
                  <p className="form-feedback">Use lowercase, numbers, and hyphens only.</p>
                )}
              </div>
              <div className="form-group">
                <label>Platform Subdomain</label>
                <div className={`platform-subdomain-group ${subdomainError ? 'has-error' : ''}`}>
                  <input 
                    type="text" 
                    placeholder={appName ? appName.toLowerCase() : "e.g. my-stack"} 
                    value={subdomainPrefix} 
                    onChange={handleSubdomainChange}
                  />
                  <span className="platform-subdomain-suffix">
                    {`-${platformSuffix}.devorants.com`}
                  </span>
                </div>
                {subdomainError ? (
                  <p className="form-feedback" style={{ color: 'var(--danger, #ef4444)', marginTop: '0.25rem', fontWeight: 500 }}>
                    {subdomainError}
                  </p>
                ) : (
                  <p className="form-feedback" style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                    Preview: <strong style={{ color: 'var(--primary, #6366f1)' }}>{`${(subdomainPrefix || appName || 'app').trim().toLowerCase().replace(/[^a-z0-9-]/g, '-')}-${platformSuffix}.devorants.com`}</strong>
                  </p>
                )}
              </div>
            </div>
            <div className="form-row-grid">
              <div className="form-group">
                <label>Custom Domain (optional)</label>
                <input 
                  type="text" 
                  placeholder="e.g. stack.example.com" 
                  value={domain} 
                  onChange={handleDomainChange}
                  style={domainError ? { borderColor: 'var(--danger, #ef4444)' } : {}}
                />
                {domainError ? (
                  <p className="form-feedback" style={{ color: 'var(--danger, #ef4444)', marginTop: '0.25rem', fontWeight: 500 }}>
                    {domainError}
                  </p>
                ) : (
                  <p className="form-feedback" style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                    Your own registered domain. Leave blank to use devorants.com subdomain.
                  </p>
                )}
              </div>
            </div>
            <div className="form-row-grid">
              <div className="form-group">
                <label>Specs (CPU & RAM) <span className="required-star">*</span></label>
                <select value={`${composeCpu}|${composeMemory}`} onChange={(e) => {
                  const [cpu, memory] = e.target.value.split('|');
                  setComposeCpu(cpu);
                  setComposeMemory(memory);
                }}>
                  <option value="0.5|256m">Micro (0.5 vCPU / 256MB RAM)</option>
                  <option value="1.0|512m">Basic (1 vCPU / 512MB RAM)</option>
                  <option value="2.0|1g">Standard (2 vCPU / 1GB RAM)</option>
                  <option value="4.0|2g">Pro (4 vCPU / 2GB RAM)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Primary App Port (Subdomain) <span className="required-star">*</span></label>
                <input type="number" placeholder="e.g. 80" required value={composeInternalPort} onChange={(e) => setComposeInternalPort(e.target.value)} />
                <p className="form-feedback">The main port your container exposes internally (e.g. 80, 3000) that the subdomain routes to.</p>
              </div>
            </div>
          </div>

          {/* Entry Points Section */}
          <div className="form-section-group">
            <div className="form-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Server size={18} />
                <h3>Entry Points (Multi-Service)</h3>
              </div>
              <button
                type="button"
                onClick={handleAddEntryPoint}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border)',
                  color: 'var(--primary)',
                  padding: '0.35rem 0.85rem',
                  borderRadius: '6px',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  transition: 'all 0.2s'
                }}
              >
                <Plus size={14} /> Add Entry Point
              </button>
            </div>
            <p className="form-feedback" style={{ marginTop: '-0.5rem', marginBottom: '0.75rem' }}>
              If your workspace runs multiple services (e.g. admin dashboard, API, website), add an entry point for each to assign separate ports and subdomains.
            </p>

            {entryPoints.length > 0 && (
              <div style={{ border: '1px solid var(--border)', borderRadius: '0.5rem', overflow: 'hidden', marginBottom: '0.5rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
                  <thead style={{ background: 'var(--surface-2, rgba(0,0,0,0.03))' }}>
                    <tr>
                      <th style={{ padding: '0.65rem 0.75rem', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>Role</th>
                      <th style={{ padding: '0.65rem 0.75rem', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>Port</th>
                      <th style={{ padding: '0.65rem 0.75rem', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>Subdomain</th>
                      <th style={{ padding: '0.65rem 0.75rem', borderBottom: '1px solid var(--border)', width: '40px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {entryPoints.map((ep, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border)' }}>
                          <input
                            type="text"
                            value={ep.role || ''}
                            placeholder="e.g. admin, api"
                            onChange={(e) => handleUpdateEntryPoint(idx, 'role', e.target.value)}
                            style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-main)', fontSize: '0.88rem', outline: 'none' }}
                          />
                        </td>
                        <td style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border)' }}>
                          <input
                            type="number"
                            value={ep.port || ''}
                            placeholder="8080"
                            onChange={(e) => handleUpdateEntryPoint(idx, 'port', e.target.value)}
                            style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-main)', fontSize: '0.88rem', outline: 'none' }}
                          />
                        </td>
                        <td style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border)' }}>
                          <input
                            type="text"
                            value={ep.domain || ''}
                            placeholder="Auto-assigned"
                            onChange={(e) => handleUpdateEntryPoint(idx, 'domain', e.target.value)}
                            style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-main)', fontSize: '0.88rem', outline: 'none' }}
                          />
                        </td>
                        <td style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border)' }}>
                          <button
                            type="button"
                            onClick={() => handleRemoveEntryPoint(idx)}
                            style={{ background: 'transparent', border: 'none', color: 'var(--danger, #dc2626)', cursor: 'pointer', padding: '0.2rem', display: 'flex' }}
                          >
                            <X size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {entryPoints.length === 0 && (
              <div style={{ padding: '1.5rem', textAlign: 'center', border: '1px dashed var(--border)', borderRadius: '0.5rem', color: 'var(--text-muted, #888)' }}>
                No additional entry points configured. Click <strong>"Add Entry Point"</strong> to map multiple services with separate subdomains.
              </div>
            )}
          </div>

          <div className="form-section-group">
            <div className="form-section-header">
              <Cloud size={18} />
              <h3>Compose Assets</h3>
            </div>
            <div className="form-group">
              <label>Upload Compose ZIP (optional)</label>
              <div className="premium-drop-zone" onClick={() => document.getElementById('composeInput').click()}>
                <Archive size={40} className="drop-icon" />
                <div className="drop-text">
                  {composeZipFile ? <b>{composeZipFile.name}</b> : "Click or drag your compose ZIP here"}
                </div>
                <input id="composeInput" type="file" accept=".zip" onChange={(e) => setComposeZipFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)} style={{ display: 'none' }} />
              </div>
              <p className="form-feedback" style={{ marginTop: '0.5rem' }}>Upload a ZIP containing your docker-compose.yml and related files. If provided, files will be extracted into the workspace.</p>
            </div>
          </div>

          <button type="submit" disabled={loading || Boolean(appNameError) || !appName.trim()} className="premium-deploy-btn" style={appNameError ? { opacity: 0.6, cursor: 'not-allowed' } : {}}>
            {loading ? <LoaderCircle size={20} className="deploy-spin" /> : <Rocket size={20} />}
            <span>{loading ? 'Initializing Workspace...' : 'Launch Workspace'}</span>
          </button>
        </form>
    </div>
  );
}
